import { useState, useImperativeHandle, forwardRef } from "react";
import { Portal } from "@mui/base";
import { useZoomStore } from "../../../stores/planner";
import { getCellWidthForZoom } from "../../../lib/DateUtils";
import PropTypes from "prop-types";
import { removeHTMLTags, linkToAnchor } from "../../../lib/StringFormat";
import { TeambookIcon } from "../../default_images/TeambookIcon";
import { icons } from "../../default_images/IconsList";

const BookingTooltip = forwardRef(({ page = "planner", disableTooltip = false }, ref) => {
  const [title, setTitle] = useState("");
  const [bookingDuration, setBookingDuration] = useState("");

  const [bookingPosition, setBookingPosition] = useState({
    left: 0,
    top: 0,
  });
  const [trianglePosition, setTrianglePosition] = useState("center");
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState([]);
  const [logWidth, setLogWidth] = useState(0);
  const [keepOpen, setKeepOpen] = useState(false);
  // fields - array with { icon: ... , text: ... }

  useImperativeHandle(ref, () => ({
    updateTooltip: (newTooltip) => {
      setOpen(newTooltip.open);

      if (newTooltip.open) {
        setTitle(newTooltip.projectName || "");
        setBookingDuration(newTooltip.bookingDuration || "");
        setBookingPosition({ left: newTooltip.left, top: newTooltip.top });

        if (newTooltip.left < TOOLTIP_WIDTH / 2) {
          setTrianglePosition("left");
        } else if (newTooltip.left + TOOLTIP_WIDTH - 30 > document.body.clientWidth) {
          setTrianglePosition("right");
        } else {
          setTrianglePosition("center");
        }

        if (page === "actuals") {
          setLogWidth(newTooltip.logWidth);

          setFields([
            {
              icon: <TeambookIcon className="tooltip-component__icon" name={icons.TASKS} />,
              text: newTooltip.occupation,
            },
            {
              icon: <TeambookIcon className="tooltip-component__icon" name={icons.PAYROLL} />,
              text: newTooltip.activity,
            },
            {
              type: "comment",
              icon: <TeambookIcon className="tooltip-component__icon" name={icons.BOOKING_COMMENT} />,
              text: removeHTMLTags(newTooltip.comment || ""),
            },
          ]);
        }

        if (page === "planner") {
          setFields([
            {
              icon: <TeambookIcon className="tooltip-component__icon" name={icons.TASKS} />,
              text: newTooltip.task?.name,
            },
            {
              icon: <TeambookIcon className="tooltip-component__icon" name={icons.CLIENTS} />,
              text: newTooltip.clientName,
            },
            {
              icon: <TeambookIcon className="tooltip-component__icon" name={icons.TEAM_USERS} />,
              text: newTooltip.team?.name,
            },
            {
              type: "comment",
              icon: <TeambookIcon className="tooltip-component__icon" name={icons.BOOKING_COMMENT} />,
              text: removeHTMLTags(newTooltip.comment || ""),
            },
          ]);
        }
      }
    },
  }));

  const positionStyles = {
    left: {
      triangle: { left: "18px", right: "auto", transform: "none" },
      triangleBg: { left: "18px", right: "auto", transform: "scale(1.11)" },
      stub: { left: "18px", right: "auto" },
    },
    right: {
      triangle: { left: "auto", right: "18px", transform: "none" },
      triangleBg: { left: "auto", right: "18px", transform: "scale(1.11)" },
      stub: { left: "auto", right: "18px" },
    },
    center: {
      triangle: { left: "50%", right: "auto", transform: "translateX(-50%)" },
      triangleBg: { left: "50%", right: "auto", transform: "translateX(-50%) scale(1.11)" },
      stub: { left: "50%", right: "auto", transform: "translateX(-50%)" },
    },
  };

  const TOOLTIP_WIDTH = 192;
  const ARROW_HEIGHT = 12;
  let tooltipHeight = page === "actuals" ? 88 : 106;

  const renderFields = fields.filter((field) => {
    if (field.text) {
      return true;
    } else {
      tooltipHeight -= 18;
      return false;
    }
  });

  const calculateLeftOffset = () => {
    if (page === "planner") {
      const { zoom } = useZoomStore();
      let cellWidth = getCellWidthForZoom(zoom);

      if (zoom === 1) {
        cellWidth = 100;
      }

      const zoomOffset = TOOLTIP_WIDTH / 2 - cellWidth / 2;
      const SCROLLBAR_WIDTH = 7;
      const PLANNER_OFFSET_X = 30;

      const overlapOffsetRight =
        cellWidth - (bookingPosition.left + cellWidth - document.body.clientWidth + PLANNER_OFFSET_X + SCROLLBAR_WIDTH);

      if (trianglePosition === "right") {
        return bookingPosition.left - TOOLTIP_WIDTH + 24 + overlapOffsetRight / 2;
      }
      if (trianglePosition === "left") {
        return bookingPosition.left + cellWidth - 24 - cellWidth / 2;
      } else {
        return bookingPosition.left - zoomOffset;
      }
    }

    if (page === "actuals") {
      if (trianglePosition === "right") {
        return bookingPosition.left - TOOLTIP_WIDTH / 2 - logWidth / 2;
      } else {
        return bookingPosition.left - TOOLTIP_WIDTH / 2 + logWidth / 2;
      }
    }
  };

  const keepTooltipOpen = () => {
    setKeepOpen(true);
  };

  return (
    <Portal>
      <div
        style={{
          left: calculateLeftOffset(),
          top:
            bookingPosition.top - tooltipHeight - ARROW_HEIGHT < 102
              ? 102
              : bookingPosition.top - tooltipHeight - ARROW_HEIGHT,
          width: TOOLTIP_WIDTH,
          visibility: keepOpen || (open && !disableTooltip) ? "visible" : "hidden",
        }}
        onMouseEnter={(e) => {
          e.preventDefault();
          keepTooltipOpen();
        }}
        onMouseLeave={(e) => {
          e.preventDefault();
          setKeepOpen(false);
        }}
        className="mobile_hidden tooltip-component"
        id="tooltip"
      >
        <div className="tooltip-component__block" style={{ height: tooltipHeight }}>
          <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <p className="tooltip-component__project-name tooltip-data__row" id="tooltip-projectName">
              {title}
            </p>
            <p
              className="tooltip-component__booking-duration"
              id="tooltip-bookingDuration"
              style={{ fontWeight: "500" }}
            >
              {bookingDuration / 60}h
            </p>
          </div>

          <div className="tooltip-component__content">
            {renderFields.map(({ icon, text, type }, i) => (
              <div key={i} className="tooltip-data__row">
                {icon}
                {type === "comment" ? <p dangerouslySetInnerHTML={{ __html: linkToAnchor(text) }} /> : <p>{text}</p>}
              </div>
            ))}
          </div>
        </div>
        <div
          className="tooltip-component__triangle"
          id="tooltip-triangle"
          style={positionStyles[trianglePosition]?.triangle} // Apply position styles to the triangle
        />
        <div
          className="tooltip-component__triangle_bg"
          id="tooltip-triangle-bg"
          style={positionStyles[trianglePosition]?.triangleBg} // Apply background position styles
        />
        <div className="tooltip-component__stub" />
      </div>
    </Portal>
  );
});

BookingTooltip.displayName = "BookingTooltip";

BookingTooltip.propTypes = {
  page: PropTypes.oneOf(["actuals", "planner"]),
  disableTooltip: PropTypes.bool,
};

export default BookingTooltip;
